/**
 * Walk an arbitrary value and return a copy with every localized
 * {@code {en, tr, ...}} object replaced by the resolved string for the
 * caller's locale. Pure — never mutates the input. Skips functions,
 * React elements, Dates, Maps, class instances — none of those are
 * authored content.
 *
 * Direct port of ThemeMaker's {@code localizeTree} in
 * {@code lms-portal/src/components/block-system/contexts.tsx}.
 *
 * Locale-key regex matches {@code en} / {@code tr} / {@code en-US} /
 * {@code pt-BR} shapes — up to 5 chars, lowercase language + optional
 * uppercase region.
 */
function isLocalizedObject(v: unknown): v is Record<string, string> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  return keys.every((k) => /^[a-z]{2}(-[A-Z]{2})?$/.test(k) && typeof obj[k] === 'string');
}

export function localizeTree(value: unknown, locale: string, fallback: string): unknown {
  if (value == null) return value;
  if (typeof value !== 'object') return value;
  // React element — leave alone.
  if ((value as { $$typeof?: unknown }).$$typeof) return value;
  // Class instance (Date, Map, custom) — not authored content.
  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return value;

  if (isLocalizedObject(value)) {
    const obj = value as Record<string, string>;
    // Resolution order: requested locale → site default (fallback) →
    // empty. We deliberately do NOT fall back to "any available locale":
    // a field authored only in `tr` must render blank on the `en` site
    // rather than leaking Turkish copy onto an English page.
    if (locale && obj[locale] != null) return obj[locale];
    if (fallback && obj[fallback] != null) return obj[fallback];
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((v) => localizeTree(v, locale, fallback));
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = localizeTree(v, locale, fallback);
  }
  return out;
}
